import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import { v4 as uuidv4 } from "uuid";
import { JSConn } from "@/utilities/js-conn";
import { format } from "date-fns";

import { NamedRts } from "@/router";
import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";

import { TOAST_OPTIONS } from "@/constants";
import { WorkOrderDetailed } from "@/store/modules/workOrders";

import { find as Rfind, propEq as RpropEq } from "ramda";

import { buildFormCommodityAutocomplete } from "@/models/Commodity";
import { buildFormClientFieldAutocomplete } from "@/models/ClientField";

import AutocompleteId from "@/components/Autocomplete/AutocompleteId.vue";
import { AutocompleteResult } from "@/components/Autocomplete/autocomplete-id";

import {
  buildFormWorkOrder,
  prettifyWorkOrderStatus,
} from "@/models/WorkOrder";

export default defineComponent({
  name: "workOrderEditForm",
  components: {
    FormErrors,
    AutocompleteId,
    LoadingAnimation,
  },
  props: {
    workOrderId: {
      type: String,
      required: true,
    },
  },
  setup(props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref("Preparing the form...");

    let firstInput: any = ref(null);

    let deleteModal: any = ref(null);
    let commodityRef: any = ref(null);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    const deleting = ref(false);

    const deleteWorkOrder = () => {
      const jsConn = new JSConn();
      jsConn.formParams = {
        id: props.workOrderId,
      };
      $store.dispatch("deleteWorkOrder", jsConn).then(jsConn => {
        ctx.root.$router.push({
          name: NamedRts.workOrders,
        });
        return Promise.resolve(jsConn);
      });
    };

    const cancelParams = () => {
      if (props.workOrderId === "new") {
        return {
          name: NamedRts.workOrders,
        };
      } else {
        return {
          name: NamedRts.workOrderDetails,
          params: {
            workOrderId: props.workOrderId,
          },
        };
      }
    };

    let {
      formWorkOrder,
      workOrderStatuses,
      loadWorkOrderStatusesIntoForm,
      loadWorkOrderIntoForm,
    } = buildFormWorkOrder();

    let { submitButtonText, submitting } = buildSubmitButton();
    let { hasFormErrors, formErrors } = buildFormErrors();

    const apiWorkOrder = computed(() => {
      return $store.getters.workOrder as WorkOrderDetailed;
    });

    let { updateCommodity } = buildFormCommodityAutocomplete();

    const localUpdateCommodity = (evt: AutocompleteResult) => {
      updateCommodity(evt, formWorkOrder.formCommodity, formWorkOrder);
    };

    const commoditiesForAutocomplete = computed(() => {
      return $store.getters
        .commoditiesForAutocompleteId as AutocompleteResult[];
    });

    let { updateClientField } = buildFormClientFieldAutocomplete();

    const selectedClientField = computed(() => {
      return $store.getters.clientFieldById(formWorkOrder.client_field_id);
    });

    const localUpdateClientField = (evt: AutocompleteResult) => {
      updateClientField(evt, formWorkOrder.formClientField, formWorkOrder);

      const scf = selectedClientField.value;

      formWorkOrder.commodity_id = scf.commodity_id;
      formWorkOrder.acres = scf.acres;

      // load the commodity
      const updatedCommodity = Rfind(
        RpropEq("id", scf.commodity_id),
        commoditiesForAutocomplete.value,
      );

      if (commodityRef.value) {
        commodityRef.value.setResultParent(updatedCommodity);
      }
    };

    const clientFieldsForAutocomplete = computed(() => {
      return $store.getters.clientFieldsForAutocomplete;
    });

    onMounted(() => {
      let dataPromises = [];
      let jsConn = new JSConn();
      const jsConnClientFields = new JSConn();
      const jsConnCommodities = new JSConn();

      jsConnCommodities.formParams = {};

      dataPromises.push(
        $store.dispatch("needClientFields", jsConnClientFields),
      );
      dataPromises.push($store.dispatch("needCommodities", jsConnCommodities));

      loadWorkOrderStatusesIntoForm(workOrderStatuses);

      if (props.workOrderId !== "new") {
        jsConn.formParams = {
          id: props.workOrderId,
        };

        dataPromises.push($store.dispatch("needWorkOrder", jsConn));
      }

      Promise.all(dataPromises)
        .then(() => {
          if (props.workOrderId !== "new") {
            loadWorkOrderIntoForm(apiWorkOrder.value, formWorkOrder);
            const rightNow = new Date();
            const formattedDate = format(rightNow, "yyyy-MM-dd");
          }
        })
        .finally(() => {
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        });
    });

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      const jsConn = new JSConn();

      hasFormErrors.value = false;
      formErrors.value = [];
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      let storeDispatch;

      if (props.workOrderId === "new") {
        formWorkOrder.id = uuidv4();
        jsConn.formParams = formWorkOrder;
        storeDispatch = "createWorkOrder";
      } else {
        jsConn.formParams = formWorkOrder;
        storeDispatch = "updateWorkOrder";
      }

      $store
        .dispatch(storeDispatch, jsConn)
        .then(() => {
          ctx.root.$bvToast.toast(
            "Work Order was successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push(cancelParams());
        })
        .catch(() => {
          hasFormErrors.value = true;
          formErrors.value = jsConn.error["response"]["data"];
        })
        .finally(() => {
          submitting.value = false;
          submitButtonText.value = "Submit";
        });
    };

    return {
      dataLoaded,
      loadingMessage,
      firstInput,
      hasFormErrors,
      formErrors,

      commodityRef,

      submitButtonText,
      submitting,
      onSubmit,

      cancelParams,

      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleteWorkOrder,
      deleting,

      apiWorkOrder,
      formWorkOrder,
      workOrderStatuses,
      prettifyWorkOrderStatus,

      commoditiesForAutocomplete,
      localUpdateCommodity,

      clientFieldsForAutocomplete,
      localUpdateClientField,
    };
  },
});
